<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Commission - ' + fixture.title"/>
    <div class="w-full mb-6">
        <FixtureHeader :fixture="fixture"></FixtureHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Commission</h3>
                                <div class="flex text-right items-end gap-3">
                                    <Button type="button" @click="emitter.emit('openAdhocCommissionSlideout', fixture)"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            :disabled="!fixture.client.cmid"
                                            :class="{'bg-pitch-grey opacity-50 cursor-not-allowed' : !fixture.client.cmid}">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create Ad-hoc Commission Invoice
                                    </Button>
                                    <Button type="button" @click="emitter.emit('openCommissionInvoice', {fixture: fixture, quote_services: form.services, organisation_id: form.organisation_id})"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            :disabled="form.services.length === 0 || !fixture.client.cmid"
                                            :class="{'text-white bg-pitch-purple': form.services.length > 0 && fixture.client.cmid, 'opacity-50 cursor-not-allowed' : form.services.length === 0 || !fixture.client.cmid}">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create Commission Invoice
                                    </Button>
                                    <search-filter :show-filter="false" v-model:value="form.search" class="w-1/2 max-w-md mr-4" @reset="reset"></search-filter>
                                </div>
                            </div>

                            <div class="flex flex-col mt-3 space-y-3">
                                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8 space-y-6">
                                        <div v-if="!fixture.client.cmid" class="rounded-md bg-red-100 p-4">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-red-400" aria-hidden="true"/>
                                                </div>
                                                <div class="ml-3 space-y-3">
                                                    <p class="text-sm text-black-700">This client is missing a CMID. Please add a CMID to create a commission invoice.</p>
                                                    <p class="text-sm text-black-700">Please email finance at <a href="mailto:sports.receipts@destinationsport.com" class="cursor-pointer underline">sports.receipts@destinationsport.com</a> to check client CMIDs that are in Unit 4.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="rounded-md bg-blue-50 p-4">
                                            <div class="flex">
                                                <div class="flex-shrink-0">
                                                    <InformationCircleIcon class="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                                </div>
                                                <div class="ml-3 space-y-3">
                                                    <p class="text-sm text-blue-700">To create a commission invoice for a
                                                        supplier, select your desired service(s) and click 'Create Commission Invoice'. <span
                                                            class="font-bold">You can only generate an invoice for one supplier at a time.</span></p>
                                                    <p class="text-sm text-blue-700">Create an <span class="underline">Ad-hoc Commission Invoice</span> for any additional commission which may be added outside of the commission set on the service lines.</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-200">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th scope="col" class="relative px-6 py-3">
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Service Name
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Service Gross Value
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Commission %
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Commission Value
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        {{ fixture?.primary_trading_details?.tax_wording || 'VAT' }} %
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        VAT Amount
                                                    </th>                                                    
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Totals
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <template v-if="Object.keys(organisations).length > 0"
                                                          v-for="(organisation, index) in organisations"
                                                          :key="index">
                                                    <tr
                                                        class="organisation-row bg-gray-200 cursor-pointer"
                                                        :data-organisation-id="organisation.id"
                                                        :class="{'  border-t border-t-gray-400' : index > 0}">
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <input v-if="organisation.cmid !== null && organisation.tax && organisation.tax.address"
                                                                   :id="'organisation-' + organisation.id"
                                                                   @change="invoiceable($event)"
                                                                   :data-organisation-id="organisation.id"
                                                                   :data-services="organisation.commission_ids"
                                                                   type="checkbox"
                                                                   class="organisation-checkbox focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"/>
                                                            <button v-if="organisation.cmid == null" title="CMID or SMID not provided for client"
                                                                class="shadow-md inline-flex mr-3 items-center px-[0.375rem] py-1 border border-transparent shadow-sm text-xs leading-4 font-medium rounded-md text-white bg-red-500 hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                                                                <ExclamationCircleIcon class="h-4 w-4" aria-hidden="true" />
                                                            </button>
                                                        </td>
                                                        <td colspan="8"
                                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{ organisation.name }}
                                                            <a :href="route('frontend.organisations.suppliers.organisation.show' , organisation.id)" 
                                                                class="shadow-md inline-flex mr-3 items-center px-[0.375rem] py-1 border border-transparent shadow-sm text-xs leading-4 font-medium rounded-md text-blue ml-4"
                                                                target="_blank">
                                                                <LinkIcon class="h-4 w-4" aria-hidden="true" />
                                                            </a>
                                                            <span class="text-red-500 text-xs pl-4" v-if="organisation.cmid == null">(Commission cannot be invoiced as this supplier has no CMID or SMID)</span>
                                                            <span class="text-red-500 text-xs pl-4" v-if="organisation.tax == null || organisation.tax.address == null">(Commission cannot be invoiced as this supplier has no tax details)</span>
                                                        </td>
                                                    </tr>
                                                    <tr class="supplier-row" :data-organisation-id="organisation.id"
                                                        :data-service-id="service.id"
                                                        v-for="(service, idx) in organisation.commissions"
                                                        :class="{'bg-gray-100': !service.used_for_invoice && idx % 2, 'bg-orange-200': service.used_for_invoice}">
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <input v-if="organisation.cmid !== null && organisation.tax && organisation.tax.address"
                                                                   :id="'service-' + service.id"
                                                                   :data-organisation-id="organisation.id"
                                                                   @change="invoiceableService($event)"
                                                                   :data-service-id="service.id" type="checkbox"
                                                                   class="service-checkbox focus:ring-indigo-500 h-4 w-4 text-pitch-purple border-gray-300 rounded"/>                                                            
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ service.name }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ formatCurrency(service.buy_gross_total, service.quote.buy_currency.code, true) }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <span v-if="service.commission_type === 'Percentage'">
                                                                {{ service.commission_amount }}%
                                                            </span>
                                                            <span v-else>
                                                                {{
                                                                    parseFloat((service.commission_total / service.buy_gross_total) * 100).toFixed(2)
                                                                }}%
                                                            </span>
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <span v-if="organisation.commission_vat_type == 'net'">
                                                                {{ formatCurrency((service.commission_total), service.quote.buy_currency.code, true) }}
                                                            </span>
                                                            <span v-else>
                                                                {{ formatCurrency((service.commission_total - service.commission_vat), service.quote.buy_currency.code, true) }}
                                                            </span>
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ (service && service.buy_vat_rate && service.buy_vat_rate.percentage != null) ? service.buy_vat_rate.percentage : 0 }}%
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{
                                                                formatCurrency(service.commission_vat, service.quote.buy_currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <span v-if="organisation.commission_vat_type == 'net'">
                                                                {{ formatCurrency(service.commission_total + service.commission_vat, service.quote.buy_currency.code, true) }}
                                                            </span>
                                                            <span v-else>
                                                                {{ formatCurrency(service.commission_total, service.quote.buy_currency.code, true) }}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr :data-organisation-id="organisation.id"
                                                        class="totals-row bg-gray-300 cursor-pointer">
                                                        <td colspan="2"
                                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            Supplier Total
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.buy_gross_total, organisation.commission_totals.buy_currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.commission_total_without_vat, organisation.commission_totals.buy_currency.code, true)
                                                            }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.vat_total, organisation.commission_totals.buy_currency.code, true)
                                                            }}                                                            
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">
                                                            {{
                                                                formatCurrency(organisation.commission_totals.commission_total, organisation.commission_totals.buy_currency.code, true)
                                                            }}
                                                        </td>
                                                    </tr>
                                                </template>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateCommissionInvoice/>
    <CreateAdhocCommissionInvoice/>
</template>


<script>
import moment from "moment";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilAltIcon,
    CheckCircleIcon,
    TrashIcon,
    ExclamationCircleIcon,
    LinkIcon
} from "@heroicons/vue/outline";
import pickBy from "lodash/pickBy";
import {emitter} from "@/eventHub";
import debounce from "lodash/debounce";
import mapValues from "lodash/mapValues";
import Button from "@/Components/Button";
import { router } from '@inertiajs/vue3';
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import SearchFilter from "@/Components/SearchFilter";
import FixtureHeader from "@/Components/FixtureHeader";
import ValidationErrors from "@/Components/ValidationErrors";
import {Link, Head, useForm, usePage} from "@inertiajs/vue3";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import CreateExpense from "@/Components/SlideOuts/CreateExpense";
import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import CreateSupplierInvoice from "@/Components/SlideOuts/CreateSupplierInvoice";
import LinkSupplierInvoiceToInvoice from "@/Components/SlideOuts/LinkSupplierInvoiceToInvoice";
import CreateCommissionInvoice from "@/Components/SlideOuts/CreateCommissionInvoice";
import CreateAdhocCommissionInvoice from "@/Components/SlideOuts/CreateAdhocCommissionInvoice";

export default {
    layout: Authenticated,
    components: {
        Link,
        Head,
        Button,
        CogIcon,
        Popover,
        LinkIcon,
        UsersIcon,
        TrashIcon,
        PlusSmIcon,
        Pagination,
        CreateQuote,
        PopoverGroup,
        SearchFilter,
        PopoverButton,
        FixtureHeader,
        PencilAltIcon,
        DuplicateQuote,
        ChevronDownIcon,
        CheckCircleIcon,
        ValidationErrors,
        ClipboardCopyIcon,
        InformationCircleIcon,
        ExclamationCircleIcon,
        CreateCommissionInvoice,
        CreateAdhocCommissionInvoice
    },
    props: {
        fixture: Object,
        organisations: Object,
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter,
            form: useForm({
                services: [],
                search: null,
                organisatation_id: null
            }),
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
    },
    methods: {
        invoiceable(event) {

            // If the event target has services, we need to check them.
            let services = event.target.dataset.services.split(',');

            services.forEach(function (service, index) {
                // Target the checkbox for this service and set it to checked.
                let service_checkbox = document.getElementById('service-' + service);

                if (event.target.checked === true) {
                    service_checkbox.checked = true
                    // Add the services to the services form array.
                    this.form.services.push(service);
                } else {
                    service_checkbox.checked = false

                    // Remove services from the form array.
                    this.form.services.splice(this.form.services.indexOf(service, 1));
                }
            }, this);

            this.form.organisation_id = event.target.dataset.organisationId;

            this.disableOtherCheckboxes(event.target.dataset.organisationId, event);

        },
        invoiceableService(event) {

            let service_checkbox = document.getElementById('service-' + event.target.dataset.serviceId);

            if (event.target.checked === true) {
                service_checkbox.checked = true
                // Add the services to the services form array.
                this.form.services.push(event.target.dataset.serviceId);
                this.form.organisation_id = event.target.dataset.organisationId;
            } else {
                service_checkbox.checked = false

                // Remove services from the form array.
                this.form.services.splice(this.form.services.indexOf(event.target.dataset.serviceId, 1));
            }

            this.disableOtherCheckboxes(event.target.dataset.organisationId, event);


        },
        disableOtherCheckboxes(organisationId, event) {
            // Disable any other checkboxes and organisations from being able to be selected.
            var all_other_checkboxes = document.querySelectorAll('input[data-organisation-id]:not(input[data-organisation-id="' + organisationId + '"])')
            var rows = document.querySelectorAll('tr[data-organisation-id]:not(tr[data-organisation-id="' + organisationId + '"])');

            all_other_checkboxes.forEach(function (service, index) {
                if (event.target.checked === true) {
                    service.setAttribute('disabled', 'disabled');
                    service.classList.add('cursor-not-allowed');
                } else {
                    service.removeAttribute('disabled');
                    service.classList.remove('cursor-not-allowed');
                }
            });

            rows.forEach(function (row, index) {
                if (event.target.checked === true) {
                    row.classList.add('opacity-50');
                    row.classList.add('cursor-not-allowed');
                } else {
                    row.classList.remove('opacity-50');
                    row.classList.remove('cursor-not-allowed');
                }
            });
        },
        reset() {
            this.form.search = null;
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('frontend.fixture.commission.index', [this.fixture.id]), pickBy(this.form), {preserveState: true, preserveScroll: true})
            }, 500),
        },
    }
}
</script>
